import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  Users,
  Plus,
  Download,
  Edit,
  Trash2,
  Eye,
  UserPlus,
  Mail,
  Phone,
  Calendar,
  Filter,
  X,
  Award,
} from "lucide-react";
import {
  getStudents,
  getStudentStats,
  deleteStudent,
} from "../../services/students";
import { getDepartments } from "../../services/departments";
import { getCourses } from "../../services/courses";
import {
  offerScholarship,
  revokeScholarship,
  getStudentScholarship,
} from "../../services/scholarships";
import { StudentWithUserInfo } from "../../types/student";
import StudentModal from "../../components/forms/StudentModal";
import ScholarshipModal from "../../components/forms/ScholarshipModal";
import StudentDetailsModal from "../../components/modals/StudentDetailsModal";
import Input from "../../components/common/Input";
import Pagination from "../../components/common/Pagination";
import { useHorizontalDragScroll } from "../../hooks/useHorizontalDragScroll";

const AdminStudents: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    | "all"
    | "active"
    | "inactive"
    | "suspended"
    | "graduated"
    | "transferred"
    | "dropped"
  >("all");
  const [departmentFilter, setDepartmentFilter] = useState<string>("all");
  const [courseFilter, setCourseFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);

  // Drag-to-scroll functionality
  const tableScrollRef = useHorizontalDragScroll<HTMLDivElement>();

  // Modal states
  const [isStudentModalOpen, setIsStudentModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isScholarshipModalOpen, setIsScholarshipModalOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(
    null,
  );
  const [editingStudent, setEditingStudent] = useState<any>(null);
  const [selectedStudentForScholarship, setSelectedStudentForScholarship] =
    useState<any>(null);

  // Handle Enter key press to trigger search
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setActiveSearchTerm(searchTerm);
      setCurrentPage(1);
    }
  };

  // Reset page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [
    activeSearchTerm,
    statusFilter,
    departmentFilter,
    courseFilter,
    pageSize,
  ]);

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Fetch departments for filter dropdown
  const { data: departmentsResponse } = useQuery({
    queryKey: ["departments"],
    queryFn: () => getDepartments(1, 100, undefined, undefined, true),
  });

  // Fetch courses for filter dropdown
  const { data: coursesResponse } = useQuery({
    queryKey: ["courses-lookup"],
    queryFn: () => getCourses({ limit: 1000 }), // Get all courses
  });

  // Fetch students data
  const {
    data: studentsResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "students",
      currentPage,
      pageSize,
      activeSearchTerm,
      statusFilter,
      departmentFilter,
      courseFilter,
    ],
    queryFn: () =>
      getStudents({
        page: currentPage,
        limit: pageSize,
        search: activeSearchTerm || undefined,
        status: statusFilter === "all" ? undefined : statusFilter,
        departmentId: departmentFilter === "all" ? undefined : departmentFilter,
        courseId: courseFilter === "all" ? undefined : courseFilter,
      }),
  });

  // Fetch student statistics
  const { data: statsResponse } = useQuery({
    queryKey: ["studentStats"],
    queryFn: () => getStudentStats(),
  });

  // Delete student mutation
  const deleteStudentMutation = useMutation({
    mutationFn: deleteStudent,
    onSuccess: () => {
      toast.success("Student deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["studentStats"] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to delete student");
    },
  });

  // Offer scholarship mutation
  const offerScholarshipMutation = useMutation({
    mutationFn: offerScholarship,
    onSuccess: () => {
      toast.success("Scholarship offered successfully");
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["student-fees"] });
      queryClient.invalidateQueries({ queryKey: ["student-fee-structures"] });
      setIsScholarshipModalOpen(false);
      setSelectedStudentForScholarship(null);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to offer scholarship",
      );
    },
  });

  // Revoke scholarship mutation
  const revokeScholarshipMutation = useMutation({
    mutationFn: revokeScholarship,
    onSuccess: () => {
      toast.success("Scholarship revoked successfully");
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["student-fees"] });
      queryClient.invalidateQueries({ queryKey: ["student-fee-structures"] });
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to revoke scholarship",
      );
    },
  });

  const students: StudentWithUserInfo[] = studentsResponse?.data || [];
  const pagination = studentsResponse?.pagination;
  const stats = statsResponse?.data;

  const handleDeleteStudent = async (studentId: string) => {
    if (!confirm("Are you sure you want to delete this student?")) return;
    deleteStudentMutation.mutate(studentId);
  };

  const handleEditStudent = (student: any) => {
    setEditingStudent(student);
    setIsStudentModalOpen(true);
  };

  const handleViewStudent = (studentId: string) => {
    setSelectedStudentId(studentId);
    setIsDetailsModalOpen(true);
  };

  const handleAddStudent = () => {
    setEditingStudent(null);
    setIsStudentModalOpen(true);
  };

  const handleOfferScholarship = (student: any) => {
    setSelectedStudentForScholarship(student);
    setIsScholarshipModalOpen(true);
  };

  const handleRevokeScholarship = async (studentId: string) => {
    if (!confirm("Are you sure you want to revoke this student's scholarship?"))
      return;
    revokeScholarshipMutation.mutate(studentId);
  };

  const closeStudentModal = () => {
    setIsStudentModalOpen(false);
    setEditingStudent(null);
  };

  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedStudentId(null);
  };

  const closeScholarshipModal = () => {
    setIsScholarshipModalOpen(false);
    setSelectedStudentForScholarship(null);
  };

  const handleEditFromDetails = () => {
    if (selectedStudentId) {
      const student = students.find((s) => s._id === selectedStudentId);
      if (student) {
        closeDetailsModal();
        handleEditStudent(student);
      }
    }
  };

  const handleExport = () => {
    console.log("Export students data");
    // TODO: Implement export functionality
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-300 rounded-xl w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-slate-300 rounded-2xl"></div>
            ))}
          </div>
          <div className="h-96 bg-slate-300 rounded-2xl"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
          <div className="text-center">
            <div className="bg-gradient-to-br from-red-300 to-red-400 rounded-2xl p-4 w-16 h-16 mx-auto shadow-lg flex items-center justify-center mb-4">
              <Users className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">
              Error loading students
            </h3>
            <p className="mt-2 text-slate-600 font-medium">
              Please try refreshing the page or contact support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 space-y-6">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-3 shadow-lg">
              <Users className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Students Management
              </h1>
              <p className="text-slate-600 font-medium mt-1">
                Manage student records and information
              </p>
            </div>
          </div>
          <button
            onClick={handleAddStudent}
            className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
          >
            <Plus className="h-5 w-5 text-emerald-500" />
            <span className="text-slate-700">Add Student</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Total Students</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {stats?.totalStudents || students.length}
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-4 shadow-lg">
              <Users className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">
                Active Students
              </p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {stats?.activeStudents ||
                  students.filter((s) => s.isActive).length}
              </p>
            </div>
            <div className="bg-gradient-to-br from-emerald-300 to-emerald-400 rounded-2xl p-4 shadow-lg">
              <UserPlus className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">New This Month</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {stats?.newEnrollmentsThisMonth || 0}
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-300 to-purple-400 rounded-2xl p-4 shadow-lg">
              <Calendar className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Inactive</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {stats?.statusCounts?.inactive ||
                  students.filter((s) => s.academicStatus !== "active").length}
              </p>
            </div>
            <div className="bg-gradient-to-br from-amber-300 to-yellow-400 rounded-2xl p-4 shadow-lg">
              <Eye className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="flex flex-col gap-4">
          {/* Top row: Search and action buttons */}
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
            <div className="flex-1 max-w-2xl">
              <Input
                type="text"
                placeholder="Search students by name, email, or student ID... (Press Enter)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="py-2 w-full"
              />
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-2.5 border-2 rounded-xl font-bold shadow-sm hover:shadow-lg transition-all duration-300 flex items-center gap-2 ${
                  showFilters ||
                  statusFilter !== "all" ||
                  departmentFilter !== "all" ||
                  courseFilter !== "all"
                    ? "bg-blue-50 border-blue-300 text-blue-700"
                    : "bg-white border-slate-200 text-slate-700"
                }`}
              >
                {showFilters ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Filter className="h-5 w-5" />
                )}
                <span className="hidden sm:inline">
                  {showFilters ? "Close" : "Filters"}
                </span>
                {(statusFilter !== "all" ||
                  departmentFilter !== "all" ||
                  courseFilter !== "all") &&
                  !showFilters && (
                    <span className="bg-blue-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {
                        [
                          statusFilter !== "all",
                          departmentFilter !== "all",
                          courseFilter !== "all",
                        ].filter(Boolean).length
                      }
                    </span>
                  )}
              </button>
              <button
                onClick={handleExport}
                className="bg-white border-2 border-slate-200 px-4 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
              >
                <Download className="h-5 w-5 text-slate-500" />
                <span className="hidden sm:inline text-slate-700">Export</span>
              </button>
            </div>
          </div>

          {/* Expandable filters section */}
          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-3 border-t border-slate-200 animate-slide-down">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value as typeof statusFilter)
                  }
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                >
                  <option value="all">All Students</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                  <option value="graduated">Graduated</option>
                  <option value="transferred">Transferred</option>
                  <option value="dropped">Dropped</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Department
                </label>
                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                >
                  <option value="all">All Departments</option>
                  {departmentsResponse?.data?.map((dept: any) => (
                    <option key={dept._id} value={dept._id}>
                      {dept.name} ({dept.code})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Course
                </label>
                <select
                  value={courseFilter}
                  onChange={(e) => setCourseFilter(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl font-medium shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                >
                  <option value="all">All Courses</option>
                  {coursesResponse?.data?.map((course: any) => (
                    <option key={course._id} value={course._id}>
                      {course.name} ({course.level})
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2 lg:col-span-3 flex justify-end">
                <button
                  onClick={() => {
                    setStatusFilter("all");
                    setDepartmentFilter("all");
                    setCourseFilter("all");
                  }}
                  className="text-sm font-bold text-slate-600 hover:text-slate-900 transition-colors"
                >
                  Clear all filters
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Students Table */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl overflow-hidden">
        <div
          ref={tableScrollRef}
          className="overflow-x-auto"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "#94a3b8 #e2e8f0",
          }}
        >
          <style>{`
            .overflow-x-auto::-webkit-scrollbar {
              height: 12px;
            }
            .overflow-x-auto::-webkit-scrollbar-track {
              background: #e2e8f0;
              border-radius: 0 0 16px 16px;
            }
            .overflow-x-auto::-webkit-scrollbar-thumb {
              background: #94a3b8;
              border-radius: 6px;
              border: 2px solid #e2e8f0;
            }
            .overflow-x-auto::-webkit-scrollbar-thumb:hover {
              background: #64748b;
            }
          `}</style>
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-gradient-to-r from-slate-50 to-blue-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Student
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Class
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Scholarship
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {students.map((student) => (
                <tr
                  key={student._id}
                  className="hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 transition-all duration-200"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-11 w-11">
                        {student.photoUrl ||
                        student.userInfo?.profileDetails?.profilePicture ? (
                          <img
                            src={
                              student.photoUrl ||
                              student.userInfo?.profileDetails?.profilePicture
                            }
                            alt={`${student.userInfo?.firstName} ${student.userInfo?.lastName}`}
                            className="h-11 w-11 rounded-xl object-cover shadow-md"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display =
                                "none";
                              (
                                e.target as HTMLImageElement
                              ).nextElementSibling?.classList.remove("hidden");
                            }}
                          />
                        ) : null}
                        <div
                          className={`h-11 w-11 rounded-xl bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center shadow-md ${
                            student.photoUrl ||
                            student.userInfo?.profileDetails?.profilePicture
                              ? "hidden"
                              : ""
                          }`}
                        >
                          <span className="text-white font-bold text-sm">
                            {student.userInfo?.firstName?.charAt(0) || ""}
                            {student.userInfo?.lastName?.charAt(0) || ""}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-bold text-slate-900">
                          {student.userInfo?.firstName || ""}{" "}
                          {student.userInfo?.lastName || ""}
                        </div>
                        <div className="text-xs text-slate-500 font-medium">
                          ADM: {student.admissionNumber || "N/A"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                    {student.classInfo?.name || "Not assigned"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                    {student.departmentInfo?.name || "Not assigned"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-3 py-1.5 text-xs font-bold rounded-xl shadow-sm ${
                        student.academicStatus === "active"
                          ? "bg-gradient-to-br from-emerald-300 to-emerald-400 text-white"
                          : "bg-gradient-to-br from-red-300 to-red-400 text-white"
                      }`}
                    >
                      {student.academicStatus || "Unknown"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                    {student.scholarshipPercentage > 0 ? (
                      <span className="bg-gradient-to-br from-purple-300 to-purple-400 text-white px-3 py-1.5 rounded-xl text-xs font-bold">
                        {student.scholarshipPercentage}%
                      </span>
                    ) : (
                      <span className="text-slate-500">None</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewStudent(student._id)}
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4 text-blue-500" />
                      </button>
                      <button
                        onClick={() => handleEditStudent(student)}
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4 text-amber-500" />
                      </button>
                      {student.scholarshipPercentage > 0 ? (
                        <button
                          onClick={() => handleRevokeScholarship(student._id)}
                          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                          title="Revoke Scholarship"
                          disabled={revokeScholarshipMutation.isPending}
                        >
                          <Award className="h-4 w-4 text-red-500" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleOfferScholarship(student)}
                          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                          title="Offer Scholarship"
                        >
                          <Award className="h-4 w-4 text-purple-500" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteStudent(student._id)}
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                        title="Delete"
                        disabled={deleteStudentMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {students.length === 0 && (
          <div className="text-center py-12">
            <div className="bg-gradient-to-br from-slate-300 to-slate-400 rounded-2xl p-4 w-16 h-16 mx-auto shadow-lg flex items-center justify-center mb-4">
              <Users className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">
              No students found
            </h3>
            <p className="mt-2 text-slate-600 font-medium">
              Try adjusting your search criteria or add a new student.
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
          <Pagination
            currentPage={currentPage}
            totalPages={pagination.totalPages}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}

      {/* Student Create/Edit Modal */}
      <StudentModal
        isOpen={isStudentModalOpen}
        onClose={closeStudentModal}
        studentId={editingStudent?._id}
      />

      {/* Student Details Modal */}
      {selectedStudentId && (
        <StudentDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={closeDetailsModal}
          studentId={selectedStudentId}
          onEdit={handleEditFromDetails}
        />
      )}

      {/* Scholarship Modal */}
      {selectedStudentForScholarship && (
        <ScholarshipModal
          isOpen={isScholarshipModalOpen}
          onClose={closeScholarshipModal}
          student={selectedStudentForScholarship}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["students"] });
          }}
        />
      )}
    </div>
  );
};

export default AdminStudents;
